import { combineReducers, Action } from 'redux';
import { all, fork, take, put } from 'redux-saga/effects';
import { channel } from 'redux-saga';
import AuthSaga from './auth/saga';
import ConfigSaga from './config/saga';
import { configReducer } from './config/reducer';
import { authReducer } from './auth/reducer';
import { AuthState, AuthActionTypes } from './auth/types';
import StudentSaga from './student/saga';
import { StudentState } from './student/types';
import { studentReducer } from './student/reducer';
import { UIState } from './ui/types';
import { uiReducer } from './ui/reducer';
import { dataReducer } from './data/reducer';
import { IDataState } from './data/types';
import DataSaga from './data/saga';
import { offersReducer } from './offers/reducer';
import { OffersState } from './offers/types';
import OffersSaga from './offers/saga';
import { websocketReducer, WebsocketState } from 'websocket-utility';

// The top-level state object
export interface ApplicationState {
  config: any;
  auth: AuthState;
  student: StudentState;
  ui: UIState;
  data: IDataState;
  offers: OffersState;
  websocket: WebsocketState;
}

// Whenever an action is dispatched, Redux will update each top-level application state property
// using the reducer with the matching name. It's important that the names match exactly, and that
// the reducer acts on the corresponding ApplicationState property type.
export const createRootReducer = () => {
  const appReducer = combineReducers<ApplicationState>({
    config: configReducer,
    auth: authReducer,
    student: studentReducer,
    ui: uiReducer,
    data: dataReducer,
    offers: offersReducer,
    websocket: websocketReducer
  });

  const rootReducer = (state: ApplicationState | undefined, action: any) => {
    if (action.type === AuthActionTypes.COGNITO_LOGOUT_SUCCESS) {
      const isTawkLoaded = state && state.ui.isTawkLoaded ? true : false;
      const clearedState: ApplicationState = appReducer(undefined, action);
      const newState = {
        ...clearedState,
        ui: {
          ...clearedState.ui,
          isLoading: false,
          isTawkLoaded
        }
      };
      // // Do not modify router reducer.
      // if (state?.router) {
      //   newState = {
      //     ...newState,
      //     router: state.router
      //   };
      // }
      return newState;
    }
    return appReducer(state, action);
  };

  return rootReducer;
};

//https://stackoverflow.com/questions/43031832/how-to-yield-put-in-redux-saga-within-a-callback
export const retryAPICallChannel = channel();

function* watchRetryAPICallChannel() {
  while (true) {
    const action: Action<any> = yield take(retryAPICallChannel);
    yield put(action);
  }
}

// Here we use `redux-saga` to trigger actions asynchronously. `redux-saga` uses something called a
// "generator function", which you can read about here:
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/function*
export function* rootSaga() {
  yield all([fork(AuthSaga)]);
  yield all([fork(StudentSaga)]);
  yield all([fork(DataSaga)]);
  yield all([fork(watchRetryAPICallChannel)]);
  yield all([fork(OffersSaga)]);
  yield all([fork(ConfigSaga)]);
}
